/**
 * Putting an image on a face.
 *
 * The model for this was already built — `PlacementLayer` in the format,
 * `addPlacement`/`drawPlacement` in the stack, and the whole geometry of
 * `shared/placement.ts` with its own tests. What was missing was any way to
 * *make* one: `addPlacement` had exactly one caller, the file loader, so a
 * placement could be restored from a saved project and never created in the
 * first place. This is that missing half.
 *
 * ## Why this is not the drop target
 *
 * Dropping a PNG on the canvas imports it as a TEMPLATE — a full-canvas artwork
 * layer — and that is the product's front door: art made in Procreate or
 * Photoshop is the expected way in rather than a fallback. Overloading the same
 * gesture to sometimes mean "place this logo on the chest" would have to guess
 * which was meant, and the obvious tell (is it 585×559?) is wrong for anyone
 * who exports their template at 2x.
 *
 * So placing is its own explicit action. A guess that is right most of the time
 * is worse here than a button, because the failure mode is silently adding the
 * wrong kind of layer to somebody's design.
 */
import type { FacePlacement, RegionId } from "../../shared/format";
import { scaleToFit } from "../../shared/placement";

/**
 * What we will take.
 *
 * SVG is deliberately absent. It draws into a canvas well enough, but it has no
 * reliable intrinsic size — an SVG with only a viewBox reports 0×0 in some
 * browsers and 300×150 in others, and both of those are a placement scaled from
 * a lie. Roblox clothing is raster at the end of the pipeline regardless.
 */
export const PLACEABLE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;

export function isPlaceableImage(file: { type: string }): boolean {
  return (PLACEABLE_TYPES as readonly string[]).includes(file.type);
}

/**
 * How much of the face a newly placed image takes.
 *
 * Not the whole face, for two reasons and the second is the practical one. It
 * should read as something placed *on* the chest rather than as something that
 * has covered it — a logo that arrives spanning edge to edge looks like a
 * mistake even when it is what was asked for. And a placement fitted exactly to
 * its face puts all four corner handles on the face boundary, where they are
 * fiddly to grab and impossible to distinguish from the face's own edge.
 */
const COMFORTABLE = 0.8;

/**
 * Where an image should land when it is first placed.
 *
 * Centred on the face, square to it, and scaled to fit inside — `scaleToFit`
 * does the fitting and already knows about aspect, so a tall logo on a wide
 * face shrinks to its height rather than overflowing.
 *
 * Pure, and separated from the decoding for that reason: this is the part with
 * a wrong answer, and it can be checked without a browser.
 */
export function initialPlacement(face: RegionId, aspect: number): FacePlacement {
  const centred: FacePlacement = { face, x: 0.5, y: 0.5, scale: 1, rotation: 0 };
  // `scaleToFit` returns the scale unchanged when it already fits, so this is
  // "shrink if you must" rather than "always resize".
  const fitted = scaleToFit(centred, aspect);
  return { ...centred, scale: fitted * COMFORTABLE };
}

export interface DecodedImage {
  image: CanvasImageSource;
  width: number;
  height: number;
  /** width / height, which is what every placement helper wants. */
  aspect: number;
}

/**
 * A file to something the stack can draw.
 *
 * `createImageBitmap` where it exists, because it decodes off the main thread —
 * a 4000px logo decoded synchronously is a visible stall on the one frame it
 * lands. The `<img>` path is the fallback, and it revokes its object URL on
 * both outcomes: a leaked blob URL holds the whole decoded image alive for the
 * life of the document, which on a tablet is how a long session runs out of
 * memory with nothing obviously wrong.
 */
export async function decodeImage(file: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return {
      image: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      aspect: bitmap.width / bitmap.height,
    };
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.addEventListener("load", () => resolve(), { once: true });
      img.addEventListener("error", () => reject(new Error("that image could not be read")), {
        once: true,
      });
      img.src = url;
    });
    return {
      image: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      aspect: img.naturalWidth / img.naturalHeight,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * A name for the layer, from the filename.
 *
 * The extension goes because it is noise in a layer list, and the result is
 * capped because a row is about 18 characters wide before it truncates — and a
 * truncated name is worse than a short one when several placements come from
 * files that differ only at the end, like `logo-final-v2-FINAL.png`.
 */
export function layerNameFor(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, "").trim();
  if (!stem) return "Image";
  return stem.length > 24 ? `${stem.slice(0, 23)}…` : stem;
}
